/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { IUsersService } from '@shared/interfaces/users-service.interface';
import { Login } from '@app/account/models/login.model';
import { LoginResponse } from '@app/account/models/login-response.model';
import { LogService } from '@logs/services/log.service';
import { map } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { URLS } from '@app/account/bavel-account.config';

const SERVICE_NAME = 'AuthService';

export const ROLES = {
  ACCEPT_REJECT_POLINES: 'ACCEPTREJECTPOLINES',
  ADVANCED_CATALOGUE: 'ADVANCEDCATALOGUE',
  CATALOGUE: 'CATALOGUE',
  CLIENT_SUPPLIER: 'ClientSupplier',
  CONTRACTS: 'Contracts',
  DOC_ATTACHMENTS: 'DOCATTACHMENTS',
  EBILLING: 'EBILLING',
  ESP: 'ESP',
  ESTAB: 'Estab',
  FALCON_LITTLE: 'FALCON_LITTLE',
  HORECA_BILLING: 'HORECABILLING',
  HORECA_BILLING_FILE_IMP: 'HORECABILLINGFILEIMP',
  INVOICE_FROM_AUTODN: 'INVOICEFROMAUTODN',
  INVOICE_FROM_PO: 'INVOICEFROMPO',
  LOGIN_EDITOR: 'LOGINEDITOR',
  MAIN_LOGIN: 'MainLogin',
  OWN_CREDIT: 'OwnCredit',
  ROOT_ENTERPRISE: 'RootEnterprise',
  SEND_ONLY_ARCHIVE: 'SENDONLYARCHIVE',
  SPONSOR: 'SPONSOR',
  SSO: 'SSO',
  TRAVEL_100_INVOICING: 'TRAVEL100INVOICING',
  TRAVEL_BILLBACK: 'TRAVELBILLBACK',
  TRAVEL_BILLING: 'TRAVELBILLING',
  TRAVEL_BILLING_FILE_IMP: 'TRAVELBILLINGFILEIMP',
  VIC: 'VIC',
  VIC_FACTURAS: 'VICFACTURAS',
  WEB_CFUE: 'WEBCFUE',
  FN_DA_RECEIVED_DOCUMENTS: 'FN_DA_RECEIVED_DOCUMENTS',
  FN_DA_SENT_DOCUMENTS: 'FN_DA_SENT_DOCUMENTS',
  FN_DA_WEB_CFUE: 'FN_DA_WEB_CFUE',
  FN_DL_AVAILABLE_DL: 'FN_DL_AVAILABLE_DL',
  FN_DL_PENDING_RQ: 'FN_DL_PENDING_RQ',
  FN_DL_DELETED: 'FN_DL_DELETED',
  FN_WBT_MANUAL_INVOICE: 'FN_WBT_MANUAL_INVOICE',
  FN_WBT_IMPORT_INVOICE: 'FN_WBT_IMPORT_INVOICE',
  FN_WBH_MANUAL_INVOICE: 'FN_WBH_MANUAL_INVOICE',
  FN_WBH_AUTO_DN: 'FN_WBH_AUTO_DN',
  FN_WBH_IMPORT_INVOICE: 'FN_WBH_IMPORT_INVOICE',
  FN_WBEPROC_MANUAL_INVOICE: 'FN_WBEPROC_MANUAL_INVOICE',
  FN_WBEPROC_IMPORT_INVOICE: 'FN_WBEPROC_IMPORT_INVOICE',
  FN_CTLG_FAMILIES: 'FN_CTLG_FAMILIES',
  FN_CTLG_CATALOGUES: 'FN_CTLG_CATALOGUES',
  FN_CTLG_PRICE_LIST: 'FN_CTLG_PRICE_LIST',
  FN_ADV_CTLG_CATALOGUE: 'FN_ADV_CTLG_CATALOGUE',
  FN_ADV_CTLG_IMPORT: 'FN_ADV_CTLG_IMPORT',
  FN_SUPPORT_DOWNLOADS: 'FN_SUPPORT_DOWNLOADS',
  FN_ROLLOUT_STATUS: 'FN_ROLLOUT_STATUS',
  FN_ROLLOUT_MATCHING_LEGACY: 'FN_ROLLOUT_MATCHING_LEGACY',
  FN_ROLLOUT_VOUCHER_SENT: 'FN_ROLLOUT_VOUCHER_SENT',
  FN_ROLLOUT_TEMPLATE_EDIT: 'FN_ROLLOUT_TEMPLATE_EDIT',
  FN_PREF_USER_PROFILE: 'FN_PREF_USER_PROFILE',
  FN_PREF_ENTREPRISE_PROFILE: 'FN_PREF_ENTREPRISE_PROFILE',
  FN_PREF_ESTAB_PROFILE: 'FN_PREF_ESTAB_PROFILE',
  FN_PREF_GENERAL: 'FN_PREF_GENERAL',
  FN_LOGIN_MANAGER: 'FN_LOGIN_MANAGER',
  FN_CONTRACTS: 'FN_CONTRACTS',
  FN_HELP: 'FN_HELP',
  FN_TPN_EBILLING_CLIENT: 'FN_TPN_EBILLING_CLIENT',
  FN_TPN_EBILLING_SUPPLIER: 'FN_TPN_EBILLING_SUPPLIER',
  FN_TPN_VCC_CLIENT: 'FN_TPN_VCC_CLIENT',
  FN_TPN_VCC_SUPPLIER: 'FN_TPN_VCC_SUPPLIER',
  FN_VCC_SEARCHER_CUSTOMER: 'FN_VCC_SEARCHER_CUSTOMER',
  FN_VCC_SEARCHER_SUPPLIER: 'FN_VCC_SEARCHER_SUPPLIER',
  FN_EPAYMENTS_CLIENT: 'FN_EPAYMENTS_CLIENT',
  FN_EPAYMENTS_SUPPLIER: 'FN_EPAYMENTS_SUPPLIER',
  FN_EPAYMENTS_EPROCUREMENT_SUPPLIER: 'FN_EPAYMENTS_EPROCUREMENT_SUPPLIER',
  UNDOCUMENTED_ESTAB: 'UNDOCUMENTED_ESTAB',
  EPAY_CLIENT_EPROCUREMENT: 'EPAY_CLIENT_EPROCUREMENT',
  EPAYMENTS_CLIENT_TRAVEL: 'EPAYMENTS_CLIENT_TRAVEL'
};

@Injectable()
export class AuthService {
  private http: HttpClient = inject(HttpClient);
  private logService: LogService = inject(LogService);
  private usersService: IUsersService = inject(IUsersService);

  public onLoggedIn$: Observable<void>;
  public onLoggedOut$: Subject<void> = new Subject<void>();

  public constructor() {
    this.onLoggedIn$ = this.usersService.onLoggedIn$;
  }

  public login(loginRequest: Login): any {
    this.logService.logInfo(`[${SERVICE_NAME}] Login called`);

    return this.http.post<LoginResponse>(URLS.LOGIN, loginRequest, { withCredentials: true })
      .pipe(map(this.parseAuthResponse.bind(this)));
  }

  public getLoggedUserProfile(): any {
    this.logService.logInfo(`[${SERVICE_NAME}] Logged user verification: cookie`);
    return this.http.get<LoginResponse>(URLS.LOGGED_USER_PROFILE)
      .pipe(map(this.parseAuthResponse.bind(this)));
  }

  public logout(): void {
    this.logoutUser();
    this.usersService.signoutRedirect();
  }

  private logoutUser() {
    this.logService.logInfo(`[${SERVICE_NAME}] Logout called`);
    window.localStorage.removeItem('token');
    this.onLoggedOut$.next();
  }

  private parseAuthResponse(loginResponse: LoginResponse): any {
    const { isAuthenticated, loginFailedReason } = loginResponse;
    if (isAuthenticated) {
      this.usersService.storeUserProfile(loginResponse);
    }
    return { isAuthenticated, loginFailedReason };
  }

  public shouldLogoutUserOnInactivity(): boolean {
    return this.usersService.shouldLogoutOnUserInactivity();
  }

  public userRole() {
    const hasSupplierRole = this.usersService.userHasRole(ROLES.FN_EPAYMENTS_SUPPLIER);
    if (hasSupplierRole) {
      return ROLES.FN_EPAYMENTS_SUPPLIER;
    }

    const hasClientRole = this.usersService.userHasRole(ROLES.EPAYMENTS_CLIENT_TRAVEL);
    if (hasClientRole) {
      return ROLES.EPAYMENTS_CLIENT_TRAVEL;
    }

    return undefined;
  }
}
